import { useState, useEffect } from 'react';
import type { CalculationQuestion as CalcQuestionType } from '@/types';

interface Props {
  question: CalcQuestionType;
  answer: string;
  onAnswer: (questionId: string, answer: string) => void;
  showResults: boolean;
  labData?: Record<string, unknown>;
}

export default function CalculationQuestion({ question, answer, onAnswer, showResults, labData }: Props) {
  const [autoFilled, setAutoFilled] = useState(false);

  // Auto-fill from lab data if dataField is specified
  useEffect(() => {
    if (question.dataField && labData && !answer && !autoFilled) {
      const value = getNestedValue(labData, question.dataField);
      if (value != null) {
        onAnswer(question.id, String(value));
        setAutoFilled(true);
      }
    }
  }, [question.dataField, labData, answer, autoFilled, onAnswer, question.id]);

  const numericAnswer = parseFloat(answer);
  const isCorrect = !isNaN(numericAnswer) && checkTolerance(numericAnswer, question.expectedValue, question.tolerance);

  return (
    <div className="quiz-question calculation-question">
      <p className="question-text">{question.question}</p>
      <div className="calc-input-row">
        <input
          type="number"
          className={`calc-input ${showResults ? (isCorrect ? 'correct' : 'incorrect') : ''}`}
          value={answer}
          onChange={(e) => !showResults && onAnswer(question.id, e.target.value)}
          placeholder="Enter your answer"
          step="any"
          disabled={showResults}
        />
        <span className="calc-unit">{question.unit}</span>
        {showResults && (
          <span className={`calc-result-icon ${isCorrect ? 'correct' : 'incorrect'}`}>
            {isCorrect ? '\u2713' : '\u2717'}
          </span>
        )}
      </div>
      {autoFilled && !showResults && (
        <div className="auto-fill-note">Auto-filled from your lab data</div>
      )}
      {showResults && (
        <div className="question-explanation">
          <strong>Expected:</strong> {question.expectedValue} {question.unit} ({'±'}{question.tolerance}%)
          {question.explanation && <div>{question.explanation}</div>}
        </div>
      )}
    </div>
  );
}

function getNestedValue(obj: Record<string, unknown>, path: string): unknown {
  return path.split('.').reduce((o: unknown, k) => {
    if (o && typeof o === 'object') return (o as Record<string, unknown>)[k];
    return undefined;
  }, obj);
}

function checkTolerance(answer: number, expected: number, tolerancePct: number): boolean {
  if (expected === 0) return Math.abs(answer) <= tolerancePct / 100;
  const diff = Math.abs((answer - expected) / expected) * 100;
  return diff <= tolerancePct;
}
